import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  startOfDay,
  endOfDay,
  subDays,
  subWeeks,
  subMonths,
} from "date-fns";
import { z } from "zod";
import type { Omittable } from "@salestrak/ui";

export type DatePresetKey =
  | "all-time"
  | "today"
  | "yesterday"
  | "this-week"
  | "last-week"
  | "mtd"
  | "last-month"
  | "ytd"
  | "custom";

export type DatePresetCustom = { startDate: string; endDate: string };

export type DatePresetValue = Omittable<{
  key: DatePresetKey;
  custom?: DatePresetCustom;
}>;

/**
 * Zod mirrors of the preset types, so consumers that persist a date preset in a
 * schema-validated config (widget cards; the forms domain later) validate it instead
 * of re-declaring the shape. The `z.enum` literals MUST stay in sync with
 * {@link DatePresetKey} — the `satisfies` check below fails the build if they drift.
 */
export const datePresetKeySchema = z.enum([
  "all-time",
  "today",
  "yesterday",
  "this-week",
  "last-week",
  "mtd",
  "last-month",
  "ytd",
  "custom",
]);
// Compile-time guard: the enum's inferred type must equal the hand-written union.
type _DatePresetKeyInSync = z.infer<typeof datePresetKeySchema> extends DatePresetKey
  ? DatePresetKey extends z.infer<typeof datePresetKeySchema>
    ? true
    : never
  : never;
const _datePresetKeyInSync: _DatePresetKeyInSync = true;
void _datePresetKeyInSync;

export const datePresetValueSchema = z.object({
  key: datePresetKeySchema,
  custom: z.object({ startDate: z.string(), endDate: z.string() }).optional(),
});

export type ResolvedDateRange = { startDate: string; endDate: string };

export const DATE_PRESETS: ReadonlyArray<{
  key: DatePresetKey;
  label: string;
  shortLabel: string;
}> = [
  { key: "all-time", label: "All Time", shortLabel: "All Time" },
  { key: "today", label: "Today", shortLabel: "Today" },
  { key: "yesterday", label: "Yesterday", shortLabel: "Yesterday" },
  { key: "this-week", label: "This Week", shortLabel: "This Week" },
  { key: "last-week", label: "Last Week", shortLabel: "Last Week" },
  { key: "mtd", label: "Month To Date", shortLabel: "MTD" },
  { key: "last-month", label: "Last Month", shortLabel: "Last Month" },
  { key: "ytd", label: "Year To Date", shortLabel: "YTD" },
  { key: "custom", label: "Custom Date Range", shortLabel: "Custom" },
];

export const DEFAULT_PRESET: DatePresetKey = "this-week";

const WEEK_OPTS = { weekStartsOn: 0 as const }; // Sunday

export function resolveDatePreset(
  key: DatePresetKey,
  custom?: DatePresetCustom,
  now: Date = new Date()
): ResolvedDateRange {
  switch (key) {
    case "all-time": {
      // No lower bound — the widest window any consumer can resolve to. Widgets
      // driven by the dashboard skip the date clause entirely for this key (see
      // `useScopedWhere`); this concrete range is the fallback for consumers that
      // must resolve to literal dates (axis labels, "vs. last period" copy).
      return { startDate: new Date(0).toISOString(), endDate: endOfDay(now).toISOString() };
    }
    case "today": {
      return { startDate: startOfDay(now).toISOString(), endDate: endOfDay(now).toISOString() };
    }
    case "yesterday": {
      const y = subDays(now, 1);
      return { startDate: startOfDay(y).toISOString(), endDate: endOfDay(y).toISOString() };
    }
    case "this-week": {
      return {
        startDate: startOfWeek(now, WEEK_OPTS).toISOString(),
        endDate: endOfWeek(now, WEEK_OPTS).toISOString(),
      };
    }
    case "last-week": {
      const lastWeek = subWeeks(now, 1);
      return {
        startDate: startOfWeek(lastWeek, WEEK_OPTS).toISOString(),
        endDate: endOfWeek(lastWeek, WEEK_OPTS).toISOString(),
      };
    }
    case "mtd": {
      return {
        startDate: startOfMonth(now).toISOString(),
        endDate: endOfDay(now).toISOString(),
      };
    }
    case "last-month": {
      const lastMonth = subMonths(now, 1);
      return {
        startDate: startOfMonth(lastMonth).toISOString(),
        endDate: endOfMonth(lastMonth).toISOString(),
      };
    }
    case "ytd": {
      return {
        startDate: startOfYear(now).toISOString(),
        endDate: endOfDay(now).toISOString(),
      };
    }
    case "custom": {
      if (!custom) {
        // No custom range yet — fall back to default preset to keep queries stable.
        return resolveDatePreset(DEFAULT_PRESET, undefined, now);
      }
      const start = parseISODate(custom.startDate);
      const end = parseISODate(custom.endDate);
      if (!start || !end) return custom;
      return {
        startDate: startOfDay(start).toISOString(),
        endDate: endOfDay(end).toISOString(),
      };
    }
  }
}

export function getPresetShortLabel(key: DatePresetKey): string {
  return DATE_PRESETS.find((p) => p.key === key)?.shortLabel ?? "";
}

export function isPresetValue(value: unknown): value is DatePresetValue {
  return (
    typeof value === "object" &&
    value !== null &&
    "key" in value &&
    typeof (value as { key: unknown }).key === "string"
  );
}

export function parseISODate(s: string | undefined | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
